import { useContext, useState } from "react";
import { LanguageContext } from "../../contexts/LanguageProvider";
import { ThemeContext } from "../../contexts/ThemeProvider";
import { motion } from "framer-motion";
import { Captcha } from "./Capthca";
import emailjs from "@emailjs/browser";
import { CheckCircle } from "lucide-react";

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.2, duration: 0.5 },
  }),
};

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
    phone: "",
    company: "",
  });

  const { t, language } = useContext(LanguageContext);
  const { colorsObj } = useContext(ThemeContext);

  // eslint-disable-next-line
  const [canSubmit, setCanSubmit] = useState(false);
  const [loading, setLoading] = useState(false);

  const [submitted, setSubmitted] = useState<boolean>(false);
  console.log(language);
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const sendMail = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (
      !process.env.REACT_APP_SERVICE_ID ||
      !process.env.REACT_APP_TEMPLATE_ID ||
      !process.env.REACT_APP_PUBLIC_KEY
    ) {
      console.error("EmailJS environment variables are not set.");
      return;
    }

    setLoading(true);
    try {
      await emailjs.send(
        process.env.REACT_APP_SERVICE_ID,
        process.env.REACT_APP_TEMPLATE_ID,
        {
          title: "New project submission",
          name: formData.name,
          message: `${formData.message}\n\nCompany: ${formData.company}\n\nEmail: ${formData.email} \n Numar telefon: ${formData.phone}`,
          email: formData.email,
          time: new Date().toLocaleString(),
        },
        process.env.REACT_APP_PUBLIC_KEY
      );

      setSubmitted(true);
      localStorage.setItem("submited", "true");

      setFormData({
        name: "",
        email: "",
        message: "",
        phone: "",
        company: "",
      });

      setCanSubmit(false);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        backgroundColor: colorsObj.colorBackground,
        padding: "4rem 1.5rem",
      }}
      className="w-full flex flex-col items-center justify-center "
    >
      <motion.h1
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.6, ease: "easeOut" }}
        style={{ color: colorsObj.colorText }}
        className="text-2xl md:text-4xl font-bold mb-8 text-center"
      >
        {t("contact-title")}
      </motion.h1>

      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5, duration: 0.6, ease: "easeOut" }}
        style={{ color: colorsObj.colorText }}
        className="text-sm md:text-base w-full md:w-5/12 mb-12 text-center"
      >
        {t("contact-subtitle")}
      </motion.p>

      <div
        className="flex items-center justify-center gap-24"
        style={{
          backgroundColor: colorsObj.colorBackground,
        }}
      >
        {submitted && (
          <motion.div
            initial="hidden"
            animate="visible"
            className="flex flex-col items-center justify-center text-center gap-6 py-16 px-4"
          >
            <CheckCircle className="text-green-500" size={80} />

            <motion.h2
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="text-3xl font-bold text-white"
            >
              {t("message-sent")}
            </motion.h2>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.5 }}
              className="text-white max-w-md"
            >
              {t("thank-you-for-message")}
            </motion.p>
          </motion.div>
        )}

        {!submitted && (
          <motion.form
            onSubmit={sendMail}
            initial="hidden"
            animate="visible"
            style={{
              backgroundColor: colorsObj.colorText + "25",
              padding: "2rem",
              borderRadius: "8px",
              color: colorsObj.colorText,
            }}
            className="flex flex-col gap-6 w-full md:w-7/12 lg:w-5/12 xl:w-4/12"
          >
            <motion.div variants={fadeInUp} custom={0}>
              <label className="block text-sm font-medium mb-1">
                {t("contact-name")}
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder={t("contact-name-placeholder")}
                className="w-full px-4 py-2  rounded-md focus:outline-none border-0 "
                style={{
                  backgroundColor: colorsObj.colorOverlay,
                  color: colorsObj.colorText,
                }}
                required
              />
            </motion.div>

            <motion.div variants={fadeInUp} custom={1}>
              <label className="block text-sm font-medium mb-1">
                {t("contact-email")}
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder={t("contact-email-placeholder")}
                className="w-full px-4 py-2  rounded-md"
                style={{
                  backgroundColor: colorsObj.colorOverlay,
                  color: colorsObj.colorText,
                }}
                required
              />
            </motion.div>

            <motion.div variants={fadeInUp} custom={1}>
              <label className="block text-sm font-medium mb-1">
                {t("contact-phone")}
              </label>
              <input
                type="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder={t("contact-phone-placeholder")}
                className="w-full px-4 py-2  rounded-md"
                style={{
                  backgroundColor: colorsObj.colorOverlay,
                  color: colorsObj.colorText,
                }}
                required
              />
            </motion.div>
            <motion.div variants={fadeInUp} custom={1}>
              <label className="block text-sm font-medium mb-1">
                {t("contact-company")}
              </label>
              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={handleChange}
                placeholder={t("contact-company-placeholder")}
                className="w-full px-4 py-2  rounded-md"
                style={{
                  backgroundColor: colorsObj.colorOverlay,
                  color: colorsObj.colorText,
                }}
                required
              />
            </motion.div>

            <motion.div variants={fadeInUp} custom={2}>
              <label className="block text-sm font-medium mb-1">
                {t("contact-message")}
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder={t("contact-message-placeholder")}
                className="w-full px-4 py-2  rounded-md h-32 resize-none "
                style={{
                  backgroundColor: colorsObj.colorOverlay,
                  color: colorsObj.colorText,
                }}
                required
              />
            </motion.div>
            <Captcha onValid={setCanSubmit} />

            <motion.button
              type="submit"
              variants={fadeInUp}
              custom={3}
              className={`${
                loading ? "bg-yellow-500/20" : "bg-yellow-500"
              } text-black px-6 py-2 rounded-full font-medium ${
                !loading ? "hover:bg-yellow-40" : ""
              } transition`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {!loading ? t("contact-submit") : "...."}
            </motion.button>
          </motion.form>
        )}
        <div className="hidden md:flex md:w-3/12  justify-end">
          <motion.img
            src={`/omnia_${language}.png`}
            alt="App on Phone"
            className="md:w-8/12 w-full"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.4 }}
            viewport={{ once: true }}
          />
        </div>
      </div>
    </div>
  );
}
